import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getComplaintsApi,
  getComplaintTopicsApi,
  updateComplaintStatusApi,
} from "../../api/appeal";
import Breadcrumb from "../../component/breadcrumb";
import Datatable from "../../component/datatable";
import Pagination from "../../component/pagination";
import {
  IComplaint,
  IComplaintFilter,
  IComplaintTopic,
} from "../../interface/appeal";
import { IUserSlim } from "../../interface/user";
import { setAppLoading } from "../../redux/app.reducer";
import { useAppDispatch } from "../../redux/hooks";
import useSnackbar from "../../utils/snackbar";
import { useNavigate } from "react-router-dom";
import { createRoot } from "react-dom/client";

const ComplaintList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const [loading, setLoading] = useState(true);
  const [complaints, setComplaints] = useState<IComplaint[]>([]);
  const [topics, setTopics] = useState<IComplaintTopic[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const defaultFilter = {
    begin: moment().format("YYYY-MM-DD"),
    end: moment().format("YYYY-MM-DD"),
    status: "",
  };
  const [filter, setFilter] = useState<IComplaintFilter>(defaultFilter);
  const filterRef = useRef<IComplaintFilter>(defaultFilter);

  const fetchTopics = async () => {
    try {
      const { data } = await getComplaintTopicsApi();
      setTopics(data ?? []);
    } catch (e: any) {
      // Silently fail - topics are optional for display
      console.error("Failed to fetch topics:", e);
    }
  };

  // Create a map for topic codes to labels
  const topicMapRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const map = topics.reduce((acc, topic) => {
      acc[topic.code] = topic.label;
      return acc;
    }, {} as Record<string, string>);
    topicMapRef.current = map;
  }, [topics]);

  const fetchComplaints = async (filter: IComplaintFilter) => {
    try {
      setLoading(true);
      const { data } = await getComplaintsApi(filter);
      setComplaints(data.results ?? []);
      setPagination({
        page: data.meta.page,
        limit: data.meta.perPage,
        total: data.meta.totalCount,
        totalPages: parseInt(
          (data.meta.totalCount / data.meta.perPage).toFixed(0)
        ),
      });
    } catch (e: any) {
      let msg = e?.response?.data?.message || "Error: Something went wrong.";
      openSnackbar(msg);
    } finally {
      setLoading(false);
    }
  }

  const handleStatusUpdate = useCallback(async (
    id: string,
    status: "resolved" | "rejected",
  ) => {
    try {
      setLoading(true);
      console.log("filter:", filterRef.current);
      await updateComplaintStatusApi(id, { status });
      await fetchComplaints(filterRef.current);
      openSnackbar("Status updated successfully");
    } catch (e: any) {
      let msg = e?.response?.data?.message || "Error: Failed to update status.";
      openSnackbar(msg);
    } finally {
      setLoading(false);
    }
  }, [filterRef]);

  useEffect(() => {
    fetchTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterRef.current = filter;
    fetchComplaints(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    dispatch(setAppLoading(loading));
  }, [loading, dispatch]);

  return (
    <>
      <Breadcrumb
        title={"ระบบร้องเรียน"}
        children={[{ title: "หน้าแรก", link: "/" }, { title: "ระบบร้องเรียน" }]}
      />

      <div className="row">
        <h4 className="mb-4 ml-3" style={{ marginTop: 5 }}>
          {moment().format("DD MMMM YYYY")}
        </h4>
        <div className="col-lg-8 my-1 ml-auto mr-3">
          <div className="d-flex align-items-center justify-content-end">
            <div className="d-flex align-items-center">
              <div className="input-group input-daterange mr-3 d-flex align-items-center">
                <input
                  type="date"
                  className="form-control"
                  value={filter.begin}
                  onChange={(e) =>
                    setFilter((prev) => ({ ...prev, begin: e.target.value }))
                  }
                  style={{ width: "300px" }}
                />
                <div className="input-group-addon mx-2">ถึง</div>
                <input
                  type="date"
                  className="form-control"
                  value={filter.end}
                  onChange={(e) =>
                    setFilter((prev) => ({ ...prev, end: e.target.value }))
                  }
                  style={{ width: "300px" }}
                />
              </div>
              <select
                className="form-control mr-3"
                value={filter.status}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, status: e.target.value }))
                }
                style={{ maxWidth: "300px" }}
              >
                <option value="">ทั้งหมด</option>
                <option value="pending">รอดำเนินการ</option>
                <option value="resolved">ดำเนินการแล้ว</option>
                <option value="rejected">ปฏิเสธ</option>
              </select>
            </div>
            <button
              className="btn btn-primary"
              onClick={() =>
                navigate(`${process.env.PUBLIC_URL}/appeal/topics`)
              }
            >
              จัดการหัวข้อร้องเรียน
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-3">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-12 mb-2">
                  <h4 className="mt-4 ml-1">
                    ทั้งหมด {pagination.total.toLocaleString("th-TH")} รายการ
                  </h4>
                </div>
              </div>
              <div>
                <Datatable
                  columns={[
                    {
                      title: "วันที่",
                      width: 150,
                      data: "submittedAt",
                      render(data: string) {
                        return moment(data).format("DD/MM/YYYY HH:mm");
                      },
                    },
                    {
                      title: "ผู้ร้องเรียน",
                      width: 200,
                      data: "user",
                      render(data: IUserSlim) {
                        return `${data.firstName} ${data.lastName}`;
                      },
                    },
                    {
                      title: "หัวข้อ",
                      width: 200,
                      data: "topicCode",
                      render(data: string) {
                        return topicMapRef.current[data] || data;
                      },
                    },
                    {
                      title: "รายละเอียด",
                      data: "description",
                      width: 250,
                      render(data: string) {
                        return `<div class="line-clamp-2">${data}</div>`;
                      },
                    },
                    {
                      title: "สถานะ",
                      width: 120,
                      data: "status",
                      render(data: string) {
                        const statusMap = {
                          pending:
                            '<span class="badge badge-warning">รอดำเนินการ</span>',
                          resolved:
                            '<span class="badge badge-success">ดำเนินการแล้ว</span>',
                          rejected:
                            '<span class="badge badge-danger">ปฏิเสธ</span>',
                        };
                        return (
                          statusMap[data as keyof typeof statusMap] || data
                        );
                      },
                    },
                    {
                      title: "การจัดการ",
                      width: 200,
                      data: null,
                      createdCell(cell, cellData, row: IComplaint) {
                        if (row.status === "pending") {
                          createRoot(cell).render(
                            <div className="d-flex">
                              <button
                                className="btn btn-success btn-sm mr-2"
                                onClick={() => {
                                  if(window.confirm("กรุณายืนยันการดำเนินการ")) handleStatusUpdate(row.id, "resolved");
                                }}
                              >
                                ดำเนินการแล้ว
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => {
                                  if(window.confirm("กรุณายืนยันการปฏิเสธ")) handleStatusUpdate(row.id, "rejected");
                                }}
                              >
                                ปฏิเสธ
                              </button>
                            </div>
                          );
                        } else {
                          createRoot(cell).render(<></>);
                        }
                      },
                    },
                  ]}
                  data={complaints}
                />

                <Pagination
                  active={pagination.page}
                  limit={pagination.limit}
                  onPageChange={(page) => {
                    setFilter((prev) => ({ ...prev, page }));
                  }}
                  pageRangeDisplayed={5}
                  total={pagination.total}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ComplaintList;
